import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const API_BASE_URL = (
  (typeof process !== "undefined" && process.env?.REACT_APP_MERCHANT_API_BASE_URL) ||
  "https://kdspanel-production.up.railway.app/api/merchant"
).replace(/\/+$/, "");

const getAuthToken = () => localStorage.getItem("userToken");
const getAuthCard = () => localStorage.getItem("card");

const redirectToLogin = () => {
  localStorage.removeItem("userToken");
  localStorage.removeItem("card");
  window.location.href = "/mlogin";
};

async function apiPost(path, body = {}) {
  const token = getAuthToken();
  if (!token) {
    redirectToLogin();
    throw new Error("Oturum bulunamadı");
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ card: getAuthCard(), ...body }),
  });

  let json = null;
  try {
    json = await response.json();
  } catch {
    // Sunucu JSON döndürmezse aşağıdaki genel hata kullanılır.
  }

  if (response.status === 401 || response.status === 403 || json?.redirectToLogin) {
    redirectToLogin();
    throw new Error("Oturum süresi doldu");
  }

  if (!response.ok || json?.success === false) {
    throw new Error(json?.message || json?.error || `HTTP_${response.status}`);
  }

  return json;
}

const CONTENT_LANGUAGE_CODES = [
  "tr", "en", "de", "fr", "es", "it", "pt", "ru", "ar", "fa", "ur", "az", "kk", "uz", "tk",
  "ky", "tg", "zh", "ja", "ko", "hi", "bn", "pa", "gu", "mr", "ne", "si", "ta", "te", "kn",
  "ml", "th", "vi", "id", "ms", "fil", "my", "km", "lo", "he", "el", "bg", "ro", "hu", "pl",
  "cs", "sk", "sl", "hr", "sr", "bs", "mk", "sq", "uk", "be", "lt", "lv", "et", "fi", "sv",
  "no", "da", "is", "nl", "af", "sw", "am", "so", "ha", "yo", "ig", "zu", "xh", "rw", "mg",
  "ka", "hy", "eu", "ca", "gl", "cy", "ga", "mt", "lb", "eo", "la", "mn", "ps", "ku", "sd",
  "jv", "su", "mi", "sm", "to", "ht"
];

const LANGUAGE_ENGLISH_NAMES = {
  tr: "Turkish",
  en: "English",
  de: "German",
  fr: "French",
  es: "Spanish",
  it: "Italian",
  pt: "Portuguese",
  ru: "Russian",
  ar: "Arabic",
  fa: "Persian",
  ur: "Urdu",
  az: "Azerbaijani",
  zh: "Chinese",
  ja: "Japanese",
  ko: "Korean",
  hi: "Hindi",
  nl: "Dutch",
  el: "Greek",
  bg: "Bulgarian",
  ro: "Romanian",
  uk: "Ukrainian",
  pl: "Polish",
  cs: "Czech",
  sv: "Swedish",
  no: "Norwegian",
  da: "Danish",
  fi: "Finnish",
  he: "Hebrew",
};

const languageDisplay = (() => {
  try {
    return new Intl.DisplayNames(["tr"], { type: "language" });
  } catch {
    return null;
  }
})();

function capitalizeLanguageLabel(value, locale = "tr") {
  const text = String(value || "").trim();
  if (!text) return "";
  try {
    return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
  } catch {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
}

function getTurkishLanguageLabel(code) {
  const localized = languageDisplay?.of(code);
  return localized && localized !== code
    ? capitalizeLanguageLabel(localized, "tr")
    : LANGUAGE_ENGLISH_NAMES[code] || code.toUpperCase();
}

// Dil seçimlerinde her dil kendi dilindeki adıyla görünür:
// Türkçe, English, Deutsch, Français, Русский, العربية vb.
function getLanguageLabel(code) {
  try {
    const localized = new Intl.DisplayNames([code], { type: "language" }).of(code);
    if (localized && localized !== code) return capitalizeLanguageLabel(localized, code);
  } catch {
    // Desteklenmeyen locale'lerde Türkçe/İngilizce fallback kullanılır.
  }
  return getTurkishLanguageLabel(code);
}

function getLanguageName(code) {
  return LANGUAGE_ENGLISH_NAMES[code] || (() => {
    try {
      return new Intl.DisplayNames(["en"], { type: "language" }).of(code) || code;
    } catch {
      return code;
    }
  })();
}

const LANGUAGE_OPTIONS = CONTENT_LANGUAGE_CODES.map((code) => ({
  code,
  label: getLanguageLabel(code),
  name: getLanguageName(code),
  turkishLabel: getTurkishLanguageLabel(code),
})).sort((a, b) => a.label.localeCompare(b.label, "tr"));

const LANGUAGE_CODE_BY_ALIAS = (() => {
  const aliases = new Map();
  LANGUAGE_OPTIONS.forEach((option) => {
    [option.code, option.label, option.name, option.turkishLabel].forEach((value) => {
      const key = String(value || "").trim().toLocaleLowerCase("tr");
      if (key) aliases.set(key, option.code);
    });
  });
  return aliases;
})();

function resolveLanguageCode(value) {
  const key = String(value || "").trim().toLocaleLowerCase("tr");
  return LANGUAGE_CODE_BY_ALIAS.get(key) || "";
}

function normalizeSpokenLanguages(values) {
  const result = [];
  const seen = new Set();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const code = resolveLanguageCode(value);
    const label = code ? getLanguageLabel(code) : String(value || "").trim();
    const uniqueKey = code || label.toLocaleLowerCase("tr");
    if (!label || seen.has(uniqueKey)) return;
    seen.add(uniqueKey);
    result.push(label);
  });
  return result;
}

const GENDER_OPTIONS = [
  { value: "female", label: "Kadın" },
  { value: "male", label: "Erkek" },
];

function normalizeGender(value) {
  const normalized = String(value || "").trim().toLocaleLowerCase("tr");
  if (["female", "woman", "kadın", "kadin"].includes(normalized)) return "female";
  if (["male", "man", "erkek"].includes(normalized)) return "male";
  return "";
}

function getGenderLabel(value) {
  return GENDER_OPTIONS.find((option) => option.value === normalizeGender(value))?.label || "Belirtilmedi";
}

const DAYS = [
  { key: "monday", label: "Pazartesi" },
  { key: "tuesday", label: "Salı" },
  { key: "wednesday", label: "Çarşamba" },
  { key: "thursday", label: "Perşembe" },
  { key: "friday", label: "Cuma" },
  { key: "saturday", label: "Cumartesi" },
  { key: "sunday", label: "Pazar" },
];

const TIME_OPTIONS = Array.from({ length: 97 }, (_, index) => {
  const minutes = index * 15;
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
});

function createEmptySchedule() {
  return DAYS.reduce((acc, day) => {
    acc[day.key] = [];
    return acc;
  }, {});
}

function normalizeSchedule(value) {
  const result = createEmptySchedule();
  DAYS.forEach(({ key }) => {
    const shifts = Array.isArray(value?.[key]) ? value[key] : [];
    result[key] = shifts
      .slice(0, 2)
      .map((shift) => ({
        start: String(shift?.start || "09:00"),
        end: String(shift?.end || "17:00"),
        // Mesai seçimleri bütün personellerde sabit 15 dakikalık adımlarla çalışır.
        intervalMinutes: 15,
      }));
  });
  return result;
}

function createLocalizedData(code = "tr") {
  return {
    languageCode: code,
    language: getLanguageName(code),
    firstName: "",
    expertise: "",
    experience: "",
    videoUrl: "",
    videoVersion: 1,
    videoUpdatedAt: null,
  };
}

function createEmptyPersonnel() {
  return {
    id: "",
    personelId: "",
    subeId: "",
    gender: "",
    coverPhotoUrl: "",
    spokenLanguages: [],
    languages: ["Turkish"],
    localizedData: [createLocalizedData("tr")],
    schedule: createEmptySchedule(),
  };
}

function mapPersonnelFromApi(person) {
  const localizedData = Array.isArray(person?.localizedData) && person.localizedData.length
    ? person.localizedData.map((item) => ({
        ...createLocalizedData(item.languageCode || "tr"),
        languageCode: item.languageCode || "tr",
        language: item.language || getLanguageName(item.languageCode || "tr"),
        firstName: item.firstName || item.name || "",
        expertise: item.expertise || "",
        experience: item.experience || "",
        videoUrl: item.videoUrl || "",
        videoVersion: Number(item.videoVersion || 1),
        videoUpdatedAt: item.videoUpdatedAt || null,
      }))
    : [
        {
          ...createLocalizedData("tr"),
          firstName: person?.firstName || "",
          expertise: person?.expertise || "",
          experience: person?.experience || "",
          videoUrl: person?.videoUrl || "",
          videoVersion: Number(person?.videoVersion || 1),
          videoUpdatedAt: person?.videoUpdatedAt || null,
        },
      ];

  return {
    ...createEmptyPersonnel(),
    ...person,
    id: person?.personelId || person?.personnelId || person?.id || person?._id || "",
    personelId: person?.personelId || person?.personnelId || person?.id || "",
    subeId: person?.subeId || person?.branchNumber || "",
    gender: normalizeGender(person?.gender || person?.cinsiyet),
    coverPhotoUrl: person?.coverPhotoUrl || "",
    spokenLanguages: normalizeSpokenLanguages(person?.spokenLanguages),
    languages: Array.isArray(person?.languages) && person.languages.length
      ? person.languages
      : localizedData.map((item) => item.language || getLanguageName(item.languageCode)),
    localizedData,
    schedule: normalizeSchedule(person?.schedule),
  };
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Dosya okunamadı"));
    reader.readAsDataURL(file);
  });
}

function LoadingSpinner({ label = "Yükleniyor..." }) {
  return (
    <div className="flex items-center justify-center gap-3 py-12 text-slate-600">
      <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
      <span className="font-semibold">{label}</span>
    </div>
  );
}

function Modal({ open, title, subtitle, onClose, children, wide = false }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    window.requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: 0, behavior: "auto" }));
  }, [open, title]);

  if (!open) return null;
  return (
    <div ref={scrollRef} className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/65 p-4 backdrop-blur-sm">
      <div className={`mx-auto my-4 ${wide ? "max-w-7xl" : "max-w-xl"} rounded-3xl bg-white shadow-2xl`}>
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 rounded-t-3xl border-b border-slate-200 bg-white/95 px-6 py-5 backdrop-blur">
          <div>
            <h2 className="text-2xl font-black text-slate-900">{title}</h2>
            {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-slate-100 px-3 py-2 text-lg font-black text-slate-600 transition hover:bg-slate-200"
            aria-label="Kapat"
          >
            ×
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function MediaPreview({ person, languageCode = "tr", compact = false, preferredMode = "" }) {
  const [viewMode, setViewMode] = useState("photo");
  const content = person.localizedData?.find((item) => item.languageCode === languageCode) || person.localizedData?.[0];
  const hasVideo = !!content?.videoUrl;
  const fullName = content?.firstName || "İsimsiz Personel";

  useEffect(() => {
    setViewMode("photo");
  }, [languageCode]);

  useEffect(() => {
    if (preferredMode === "video" && hasVideo) {
      setViewMode("video");
    } else if (preferredMode === "photo") {
      setViewMode("photo");
    }
  }, [preferredMode, hasVideo, content?.videoUrl]);

  return (
    <div className={`relative overflow-hidden rounded-2xl bg-slate-900 shadow-lg ${compact ? "w-full" : "mx-auto w-full max-w-[280px]"}`} style={{ aspectRatio: "9 / 16" }}>
      {viewMode === "video" && hasVideo ? (
        <video className="h-full w-full object-cover" src={content.videoUrl} controls autoPlay preload="metadata" poster={person.coverPhotoUrl || undefined} />
      ) : person.coverPhotoUrl ? (
        <img className="h-full w-full object-cover" src={person.coverPhotoUrl} alt={fullName} />
      ) : (
        <div className="flex h-full items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-700 px-5 text-center text-lg font-bold text-white">
          9:16 ortak kapak fotoğrafı yükleyin
        </div>
      )}

      <div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-black/80 to-transparent p-4 pb-12">
        <div className="max-w-[60%] text-lg font-black text-white drop-shadow">{fullName}</div>
      </div>

      <div className={`absolute right-3 top-3 z-20 flex rounded-xl bg-black/65 p-1 backdrop-blur ${compact ? "text-[10px]" : "text-xs"}`}>
        <button
          type="button"
          onClick={() => setViewMode("photo")}
          className={`rounded-lg px-2.5 py-1.5 font-black transition ${viewMode === "photo" ? "bg-white text-slate-900" : "text-white hover:bg-white/15"}`}
        >
          Fotoğraf
        </button>
        <button
          type="button"
          onClick={() => hasVideo && setViewMode("video")}
          disabled={!hasVideo}
          className={`rounded-lg px-2.5 py-1.5 font-black transition ${viewMode === "video" ? "bg-white text-purple-700" : "text-white hover:bg-white/15"} disabled:cursor-not-allowed disabled:opacity-40`}
        >
          Video
        </button>
      </div>
    </div>
  );
}

function timeToMinutes(value) {
  const [hour, minute] = String(value || "00:00").split(":").map(Number);
  return (Number.isFinite(hour) ? hour : 0) * 60 + (Number.isFinite(minute) ? minute : 0);
}

function minutesToTime(value) {
  const minutes = Math.max(0, Math.min(1440, Number(value) || 0));
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function ScheduleEditor({ schedule, onChange }) {
  const SLOT_MINUTES = 15;
  const updateDay = (dayKey, shifts) => onChange({
    ...schedule,
    [dayKey]: shifts.slice(0, 2).map((shift) => ({ ...shift, intervalMinutes: SLOT_MINUTES })),
  });

  const addShift = (dayKey) => {
    const shifts = Array.isArray(schedule[dayKey]) ? schedule[dayKey] : [];
    if (shifts.length >= 2) return;

    if (shifts.length === 0) {
      updateDay(dayKey, [{ start: "09:00", end: "17:00", intervalMinutes: SLOT_MINUTES }]);
      return;
    }

    const startMinutes = timeToMinutes(shifts[0].end);
    const endMinutes = startMinutes + SLOT_MINUTES;
    if (endMinutes > 1440) return;

    updateDay(dayKey, [
      ...shifts,
      {
        start: minutesToTime(startMinutes),
        end: minutesToTime(endMinutes),
        intervalMinutes: SLOT_MINUTES,
      },
    ]);
  };

  const removeShift = (dayKey, index) => {
    const shifts = [...(schedule[dayKey] || [])];
    shifts.splice(index, 1);
    updateDay(dayKey, shifts);
  };

  const changeShift = (dayKey, index, field, value) => {
    const shifts = [...(schedule[dayKey] || [])];
    const current = { ...shifts[index], [field]: value, intervalMinutes: SLOT_MINUTES };

    if (field === "start") {
      const minimumEnd = timeToMinutes(value) + SLOT_MINUTES;
      if (timeToMinutes(current.end) < minimumEnd) current.end = minutesToTime(minimumEnd);
    }

    shifts[index] = current;
    updateDay(dayKey, shifts);
  };

  const isStartOptionDisabled = (shifts, index, time) => {
    const candidate = timeToMinutes(time);
    const previousEnd = index > 0 ? timeToMinutes(shifts[index - 1]?.end) : 0;
    const nextStart = index < shifts.length - 1 ? timeToMinutes(shifts[index + 1]?.start) : 1440;

    if (candidate < previousEnd) return true;
    if (candidate + SLOT_MINUTES > 1440) return true;
    if (index < shifts.length - 1 && candidate + SLOT_MINUTES > nextStart) return true;
    return false;
  };

  const isEndOptionDisabled = (shifts, index, time) => {
    const candidate = timeToMinutes(time);
    const startMinutes = timeToMinutes(shifts[index]?.start);
    const nextStart = index < shifts.length - 1 ? timeToMinutes(shifts[index + 1]?.start) : 1440;

    // Bitiş, başlangıçtan en az 15 dakika sonra olmalıdır.
    if (candidate < startMinutes + SLOT_MINUTES) return true;
    // İlk mesai ikinci mesainin başlangıcını geçemez.
    if (index < shifts.length - 1 && candidate > nextStart) return true;
    return false;
  };

  return (
    <div className="space-y-3">
      {DAYS.map((day) => {
        const shifts = schedule[day.key] || [];
        const firstShiftEnd = shifts[0] ? timeToMinutes(shifts[0].end) : 0;
        const canAdd = shifts.length === 0 || (shifts.length === 1 && firstShiftEnd + SLOT_MINUTES <= 1440);

        return (
          <div key={day.key} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-black text-slate-800">{day.label}</div>
                <div className="text-xs text-slate-500">Saatler sabit 15 dakikalık aralıklarla seçilir. İkinci mesai yalnızca ilk mesainin bitişinden sonra başlayabilir.</div>
              </div>
              <button
                type="button"
                onClick={() => addShift(day.key)}
                disabled={!canAdd}
                className="rounded-xl bg-indigo-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                + Mesai Ekle
              </button>
            </div>

            {shifts.length === 0 ? (
              <div className="mt-3 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-slate-500">Bu gün çalışmıyor.</div>
            ) : (
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                {shifts.map((shift, index) => (
                  <div key={`${day.key}-${index}`} className="rounded-xl border border-slate-200 bg-white p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-black text-slate-700">{index + 1}. Mesai</span>
                      <button type="button" onClick={() => removeShift(day.key, index)} className="text-xs font-bold text-rose-600 hover:text-rose-700">Kaldır</button>
                    </div>
                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                      <select value={shift.start} onChange={(event) => changeShift(day.key, index, "start", event.target.value)} className="rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-indigo-500">
                        {TIME_OPTIONS.slice(0, -1).map((time) => {
                          const disabled = isStartOptionDisabled(shifts, index, time);
                          return <option key={time} value={time} disabled={disabled} className={disabled ? "bg-slate-900 text-slate-400" : "bg-white text-slate-900"}>{time}</option>;
                        })}
                      </select>
                      <span className="font-bold text-slate-400">—</span>
                      <select value={shift.end} onChange={(event) => changeShift(day.key, index, "end", event.target.value)} className="rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-indigo-500">
                        {TIME_OPTIONS.slice(1).map((time) => {
                          const disabled = isEndOptionDisabled(shifts, index, time);
                          return <option key={time} value={time} disabled={disabled} className={disabled ? "bg-slate-900 text-slate-400" : "bg-white text-slate-900"}>{time}</option>;
                        })}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PersonnelForm({ branch, initialValue, onClose, onSaved }) {
  const [form, setForm] = useState(() => initialValue ? mapPersonnelFromApi(initialValue) : createEmptyPersonnel());
  const [activeLanguage, setActiveLanguage] = useState(form.localizedData?.[0]?.languageCode || "tr");
  const [newLanguageCode, setNewLanguageCode] = useState("en");
  const [spokenLanguageCode, setSpokenLanguageCode] = useState("en");
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");
  const [error, setError] = useState("");
  const [previewMode, setPreviewMode] = useState("photo");

  const activeContent = form.localizedData.find((item) => item.languageCode === activeLanguage) || form.localizedData[0];

  const updateActiveContent = (patch) => {
    setForm((current) => ({
      ...current,
      localizedData: current.localizedData.map((item) => item.languageCode === activeLanguage ? { ...item, ...patch } : item),
    }));
  };

  const addContentLanguage = () => {
    if (form.localizedData.some((item) => item.languageCode === newLanguageCode)) {
      setActiveLanguage(newLanguageCode);
      return;
    }
    const languageName = getLanguageName(newLanguageCode);
    const codesAfterAdd = new Set([...form.localizedData.map((item) => item.languageCode), newLanguageCode]);
    const nextUnused = LANGUAGE_OPTIONS.find((option) => !codesAfterAdd.has(option.code));
    setForm((current) => ({
      ...current,
      languages: Array.from(new Set([...(current.languages || []), languageName])),
      localizedData: [...current.localizedData, createLocalizedData(newLanguageCode)],
    }));
    setActiveLanguage(newLanguageCode);
    setPreviewMode("photo");
    if (nextUnused) setNewLanguageCode(nextUnused.code);
  };

  const removeContentLanguage = (code) => {
    if (code === "tr") {
      setError("Türkçe standart dil olduğu için kaldırılamaz.");
      return;
    }
    const languageName = getLanguageName(code);
    const remaining = form.localizedData.filter((item) => item.languageCode !== code);
    setForm((current) => ({
      ...current,
      localizedData: remaining,
      languages: (current.languages || []).filter((item) => item !== languageName),
    }));
    if (activeLanguage === code) {
      setActiveLanguage("tr");
      setPreviewMode("photo");
    }
  };

  const addSpokenLanguage = () => {
    const label = getLanguageLabel(spokenLanguageCode);
    const usedCodes = new Set(
      (form.spokenLanguages || [])
        .map((value) => resolveLanguageCode(value))
        .filter(Boolean)
    );
    usedCodes.add(spokenLanguageCode);
    const nextUnused = LANGUAGE_OPTIONS.find((option) => !usedCodes.has(option.code));
    setForm((current) => ({
      ...current,
      spokenLanguages: normalizeSpokenLanguages([...(current.spokenLanguages || []), label]),
    }));
    if (nextUnused) setSpokenLanguageCode(nextUnused.code);
  };

  const uploadFile = async (file, kind, languageCode = "") => {
    if (!file) return;
    const uploadKey = `${kind}:${languageCode || "common"}`;
    setUploadingKey(uploadKey);
    setError("");
    try {
      const dataUrl = await fileToDataUrl(file);
      const json = await apiPost("/uploadstaffmedia", {
        base64: dataUrl,
        contentType: file.type,
        filename: file.name,
        mediaKind: kind,
        subeId: branch.subeId || branch.branchNumber,
        personelId: form.personelId || form.id || "new",
        languageCode: languageCode || "common",
      });
      const url = json.url || json.publicUrl || json.result?.url;
      if (!url) throw new Error("Yükleme URL'si alınamadı");

      if (kind === "cover") {
        setForm((current) => ({ ...current, coverPhotoUrl: url }));
        setPreviewMode("photo");
      } else if (kind === "video") {
        updateActiveContent({ videoUrl: url, videoUpdatedAt: new Date().toISOString() });
        setPreviewMode("video");
      }
    } catch (uploadError) {
      setError(uploadError.message || "Dosya yüklenemedi");
    } finally {
      setUploadingKey("");
    }
  };

  const validate = () => {
    if (!normalizeGender(form.gender)) return "Lütfen personelin cinsiyetini seçin.";

    const turkish = form.localizedData.find((item) => item.languageCode === "tr");
    if (!turkish?.firstName.trim()) return "Türkçe verilerde isim zorunludur.";

    for (const item of form.localizedData) {
      if (!item.firstName.trim()) {
        return `${getLanguageLabel(item.languageCode)} verilerinde isim zorunludur.`;
      }
    }

    for (const day of DAYS) {
      const shifts = form.schedule[day.key] || [];
      let previousEnd = 0;
      for (let index = 0; index < shifts.length; index += 1) {
        const shift = shifts[index];
        const intervalMinutes = 15;
        const startMinutes = timeToMinutes(shift.start);
        const endMinutes = timeToMinutes(shift.end);

        if (!shift.start || !shift.end || startMinutes >= endMinutes) {
          return `${day.label} mesai başlangıç/bitiş saatini kontrol edin.`;
        }
        if (endMinutes - startMinutes < intervalMinutes) {
          return `${day.label} ${index + 1}. mesainin bitişi, başlangıçtan en az ${intervalMinutes} dakika sonra olmalıdır.`;
        }
        if (index > 0 && startMinutes < previousEnd) {
          return `${day.label} ${index + 1}. mesai, önceki mesainin bitişinden önce başlayamaz.`;
        }
        previousEnd = endMinutes;
      }
    }
    return "";
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        personelId: form.personelId || form.id || undefined,
        subeId: branch.subeId || branch.branchNumber,
        gender: normalizeGender(form.gender),
        coverPhotoUrl: form.coverPhotoUrl,
        spokenLanguages: form.spokenLanguages,
        languages: form.localizedData.map((item) => item.language || getLanguageName(item.languageCode)),
        localizedData: form.localizedData.map((item) => ({
          languageCode: item.languageCode,
          language: item.language || getLanguageName(item.languageCode),
          firstName: item.firstName || "",
          expertise: item.expertise || "",
          experience: item.experience || "",
          videoUrl: item.videoUrl || "",
          videoVersion: Number(item.videoVersion || 1),
          videoUpdatedAt: item.videoUpdatedAt || null,
        })),
        schedule: form.schedule,
      };
      const json = await apiPost("/savepersonnel", payload);
      onSaved(json.personnel);
    } catch (saveError) {
      setError(saveError.message || "Personel kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 font-semibold text-rose-700">{error}</div> : null}

      <div className="grid gap-7 xl:grid-cols-[300px_1fr]">
        <div className="space-y-5">
          <MediaPreview person={form} languageCode={activeLanguage} preferredMode={previewMode} />

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="font-black text-slate-800">Kapak Fotoğrafı</div>
            <div className="mt-1 text-xs text-slate-500">Dillerden bağımsızdır, tüm dillerde aynı 9:16 kapak kullanılır.</div>
            <label className="mt-3 block cursor-pointer rounded-xl bg-slate-900 px-4 py-3 text-center text-sm font-bold text-white transition hover:bg-slate-700">
              {uploadingKey === "cover:common" ? "Yükleniyor..." : "Kapak Fotoğrafı Yükle"}
              <input type="file" accept="image/*" className="hidden" disabled={!!uploadingKey} onChange={(event) => uploadFile(event.target.files?.[0], "cover")} />
            </label>
          </div>
        </div>

        <div className="space-y-6">
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-xl font-black text-slate-900">Cinsiyet</h3>
            <p className="mt-1 text-sm text-slate-500">Personel için Kadın veya Erkek seçimini işaretleyin.</p>
            <div className="mt-4 grid max-w-xl grid-cols-2 gap-3">
              {GENDER_OPTIONS.map((option) => {
                const selected = form.gender === option.value;
                return (
                  <label
                    key={option.value}
                    className={`flex cursor-pointer items-center justify-center gap-3 rounded-2xl border-2 px-5 py-4 font-black transition ${selected ? "border-indigo-600 bg-indigo-50 text-indigo-800" : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300"}`}
                  >
                    <input
                      type="radio"
                      name="personnel-gender"
                      value={option.value}
                      checked={selected}
                      onChange={(event) => setForm((current) => ({ ...current, gender: event.target.value }))}
                      className="h-5 w-5 accent-indigo-600"
                    />
                    {option.label}
                  </label>
                );
              })}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-black text-slate-900">Dil Bazlı Personel Verileri</h3>
                <p className="mt-1 text-sm text-slate-500">Konuştuğu dillerden ayrıdır. Buradaki her dil için isim, uzmanlık ve video ayrı kaydedilir.</p>
              </div>
              <div className="flex min-w-[260px] gap-2">
                <select value={newLanguageCode} onChange={(event) => setNewLanguageCode(event.target.value)} className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-indigo-500">
                  {LANGUAGE_OPTIONS.map((option) => <option key={option.code} value={option.code}>{option.label}</option>)}
                </select>
                <button type="button" onClick={addContentLanguage} className="rounded-xl bg-indigo-600 px-4 py-2 font-bold text-white hover:bg-indigo-700">Dil Ekle</button>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {form.localizedData.map((item) => (
                <div key={item.languageCode} className={`flex overflow-hidden rounded-xl border ${activeLanguage === item.languageCode ? "border-indigo-600 bg-indigo-50" : "border-slate-200 bg-white"}`}>
                  <button type="button" onClick={() => { setActiveLanguage(item.languageCode); setPreviewMode("photo"); }} className="px-4 py-2 text-sm font-black text-slate-800">
                    {getLanguageLabel(item.languageCode)}
                  </button>
                  {item.languageCode !== "tr" ? (
                    <button type="button" onClick={() => removeContentLanguage(item.languageCode)} className="border-l border-inherit px-3 text-rose-600 hover:bg-rose-50">×</button>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-2xl bg-indigo-50 px-4 py-3 text-sm font-bold text-indigo-800">
              {getLanguageLabel(activeLanguage)} dilinde çıkacak olan veriler
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-3">
              <label className="space-y-2">
                <span className="text-sm font-black text-slate-700">İsim</span>
                <input value={activeContent?.firstName || ""} onChange={(event) => updateActiveContent({ firstName: event.target.value })} className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500" placeholder="İsim" />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-black text-slate-700">Uzmanlık Alanı</span>
                <input value={activeContent?.expertise || ""} onChange={(event) => updateActiveContent({ expertise: event.target.value })} className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500" placeholder="Örn. Soft masaj" />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-black text-slate-700">Deneyim</span>
                <input value={activeContent?.experience || ""} onChange={(event) => updateActiveContent({ experience: event.target.value })} className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500" placeholder="Örn. 8 yıl" />
              </label>
            </div>

            <div className="mt-5 rounded-2xl border-2 border-purple-200 bg-purple-50 p-4">
              <div className="font-black text-slate-800">Bu Dilin Videosu</div>
              <p className="mt-1 text-xs text-slate-500">Fotoğraf tüm dillerde ortak kapak fotoğrafıdır. Burada yalnızca seçili dile ait video yüklenir.</p>
              <label className="mt-3 block cursor-pointer rounded-xl bg-white px-4 py-3 text-center text-sm font-bold text-purple-700 shadow-sm ring-1 ring-purple-200 hover:bg-purple-100">
                {uploadingKey === `video:${activeLanguage}` ? "Yükleniyor..." : `${getLanguageLabel(activeLanguage)} Videosu Yükle`}
                <input type="file" accept="video/*" className="hidden" disabled={!!uploadingKey} onChange={(event) => uploadFile(event.target.files?.[0], "video", activeLanguage)} />
              </label>
              <div className="mt-2 text-xs text-slate-500">videoUrl: {activeContent?.videoUrl ? "hazır" : "boş"} · version: {Number(activeContent?.videoVersion || 1)}</div>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-xl font-black text-slate-900">Konuştuğu Diller</h3>
            <p className="mt-1 text-sm text-slate-500">Bu alan içerik dillerinden bağımsızdır; personelin gerçekten konuşabildiği dilleri seçin.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {form.spokenLanguages.map((language) => (
                <span key={language} className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-2 text-sm font-black text-emerald-800">
                  {language}
                  <button type="button" onClick={() => setForm((current) => ({ ...current, spokenLanguages: current.spokenLanguages.filter((item) => item !== language) }))} className="text-emerald-900">×</button>
                </span>
              ))}
            </div>
            <div className="mt-4 flex max-w-xl gap-2">
              <select value={spokenLanguageCode} onChange={(event) => setSpokenLanguageCode(event.target.value)} className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-emerald-500">
                {LANGUAGE_OPTIONS.map((option) => <option key={option.code} value={option.code}>{option.label}</option>)}
              </select>
              <button type="button" onClick={addSpokenLanguage} className="rounded-xl bg-emerald-600 px-4 py-2 font-bold text-white hover:bg-emerald-700">Ekle</button>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-xl font-black text-slate-900">Çalışma Takvimi</h3>
            <p className="mt-1 text-sm text-slate-500">Her gün için 0, 1 veya 2 ayrı mesai ekleyebilirsiniz. Tüm başlangıç ve bitiş saatleri sabit 15 dakikalık aralıklarla seçilir.</p>
            <div className="mt-5"><ScheduleEditor schedule={form.schedule} onChange={(schedule) => setForm((current) => ({ ...current, schedule }))} /></div>
          </section>
        </div>
      </div>

      <div className="sticky bottom-0 flex flex-wrap justify-end gap-3 rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur">
        <button type="button" onClick={onClose} disabled={saving} className="rounded-xl border border-slate-300 px-5 py-3 font-bold text-slate-700 hover:bg-slate-50">Vazgeç</button>
        <button type="button" onClick={handleSave} disabled={saving || !!uploadingKey} className="rounded-xl bg-indigo-600 px-6 py-3 font-black text-white shadow-lg hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300">
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>
    </div>
  );
}

function BranchList({ branches, loading, onOpen, onEdit, onDelete, onNew }) {
  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900">Şubeler</h1>
          <p className="mt-1 text-slate-500">Şubelerinizi oluşturun, şehir bilgilerini güncelleyin ve personelleri yönetin.</p>
        </div>
        <button onClick={onNew} className="rounded-2xl bg-indigo-600 px-5 py-3 font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-indigo-700">+ Yeni Şube Kaydet</button>
      </div>

      {loading ? <LoadingSpinner label="Şubeler yükleniyor..." /> : branches.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-slate-300 bg-white px-6 py-16 text-center">
          <div className="text-5xl">🏢</div>
          <h2 className="mt-4 text-xl font-black text-slate-800">Henüz şube yok</h2>
          <p className="mt-2 text-slate-500">İlk şubenizi oluşturduğunuzda otomatik olarak benzersiz 30 haneli numara atanır.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {branches.map((branch) => (
            <article key={branch.subeId || branch.branchNumber} className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:border-indigo-300 hover:shadow-xl">
              <button type="button" onClick={() => onOpen(branch)} className="block w-full p-5 text-left">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="truncate text-xl font-black text-slate-900">{branch.name}</div>
                    <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-slate-500"><span>📍</span>{branch.city}</div>
                  </div>
                  <div className="rounded-xl bg-indigo-50 px-3 py-2 text-indigo-700">→</div>
                </div>
                <div className="mt-5 rounded-xl bg-slate-50 px-3 py-2 font-mono text-[11px] text-slate-500">
                  {branch.subeId || branch.branchNumber}
                </div>
                <div className="mt-3 text-sm font-black text-indigo-700">Personelleri Aç</div>
              </button>
              <div className="flex border-t border-slate-100">
                <button type="button" onClick={() => onEdit(branch)} className="flex-1 px-4 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50">Düzenle</button>
                <button type="button" onClick={() => onDelete(branch)} className="flex-1 border-l border-slate-100 px-4 py-3 text-sm font-bold text-rose-600 hover:bg-rose-50">Sil</button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function PersonnelCard({ person, onEdit, onDelete }) {
  const defaultLanguageCode = person.localizedData?.find((item) => item.languageCode === "tr")?.languageCode
    || person.localizedData?.[0]?.languageCode
    || "tr";
  const [selectedLanguageCode, setSelectedLanguageCode] = useState(defaultLanguageCode);
  const selectedContent = person.localizedData?.find((item) => item.languageCode === selectedLanguageCode)
    || person.localizedData?.[0];

  return (
    <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
      <MediaPreview person={person} languageCode={selectedLanguageCode} compact />
      <div className="p-4">
        <div className="flex flex-wrap gap-1.5">
          {(person.localizedData || []).map((item) => (
            <button
              type="button"
              key={item.languageCode}
              onClick={() => setSelectedLanguageCode(item.languageCode)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-black transition ${selectedLanguageCode === item.languageCode ? "bg-indigo-600 text-white" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"}`}
            >
              {getLanguageLabel(item.languageCode)}
            </button>
          ))}
        </div>
        <div className="mt-3 text-sm font-bold text-slate-700">{selectedContent?.expertise || "Uzmanlık belirtilmedi"}</div>
        <div className="mt-1 text-xs text-slate-500">{selectedContent?.experience || "Deneyim belirtilmedi"}</div>
        <div className="mt-3 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-700">{getGenderLabel(person.gender)}</div>
        <div className="mt-3 text-xs text-slate-500">Konuştuğu diller: {(person.spokenLanguages || []).join(", ") || "Belirtilmedi"}</div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button type="button" onClick={() => onEdit(person)} className="rounded-xl bg-slate-100 px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-200">Düzenle</button>
          <button type="button" onClick={() => onDelete(person)} className="rounded-xl bg-rose-50 px-3 py-2 text-sm font-black text-rose-700 hover:bg-rose-100">Sil</button>
        </div>
      </div>
    </article>
  );
}

function PersonnelList({ branch, personnel, loading, onBack, onNew, onEdit, onDelete }) {
  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <button type="button" onClick={onBack} className="mb-3 text-sm font-black text-indigo-700 hover:text-indigo-900">← Şubelere Dön</button>
          <h1 className="text-3xl font-black tracking-tight text-slate-900">{branch.name} · Personeller</h1>
          <p className="mt-1 text-slate-500">{branch.city} · Şube No: <span className="font-mono text-xs">{branch.subeId || branch.branchNumber}</span></p>
        </div>
        <button onClick={onNew} className="rounded-2xl bg-emerald-600 px-5 py-3 font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-emerald-700">+ Personel Ekle</button>
      </div>

      {loading ? <LoadingSpinner label="Personeller yükleniyor..." /> : personnel.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-slate-300 bg-white px-6 py-16 text-center">
          <div className="text-5xl">👤</div>
          <h2 className="mt-4 text-xl font-black text-slate-800">Bu şubede personel bulunmuyor</h2>
          <p className="mt-2 text-slate-500">Dil bazlı profil, ortak 9:16 kapak fotoğrafı, dil videoları ve çalışma takvimi ekleyebilirsiniz.</p>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {personnel.map((person) => (
            <PersonnelCard key={person.id} person={person} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function BranchesPage() {
  const navigate = useNavigate();
  const { branchNumber } = useParams();
  const [branches, setBranches] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);
  const [pageError, setPageError] = useState("");
  const [toast, setToast] = useState("");
  const [branchModal, setBranchModal] = useState(null);
  const [branchForm, setBranchForm] = useState({ name: "", city: "" });
  const [branchSaving, setBranchSaving] = useState(false);
  const [personnelModal, setPersonnelModal] = useState(null);

  const activeBranch = useMemo(() => branches.find((item) => (item.subeId || item.branchNumber) === branchNumber) || null, [branches, branchNumber]);

  const loadBranches = useCallback(async () => {
    setLoadingBranches(true);
    setPageError("");
    try {
      const json = await apiPost("/getbranches");
      setBranches(Array.isArray(json.branches) ? json.branches : []);
    } catch (error) {
      setPageError(error.message || "Şubeler alınamadı");
    } finally {
      setLoadingBranches(false);
    }
  }, []);

  const loadPersonnel = useCallback(async (number) => {
    if (!number) return;
    setLoadingPersonnel(true);
    setPageError("");
    try {
      const json = await apiPost("/getpersonnel", { subeId: number });
      setPersonnel((Array.isArray(json.personnel) ? json.personnel : []).map(mapPersonnelFromApi));
    } catch (error) {
      setPageError(error.message || "Personeller alınamadı");
    } finally {
      setLoadingPersonnel(false);
    }
  }, []);

  useEffect(() => { loadBranches(); }, [loadBranches]);
  useEffect(() => { if (branchNumber) loadPersonnel(branchNumber); else setPersonnel([]); }, [branchNumber, loadPersonnel]);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const openNewBranch = () => {
    setBranchForm({ name: "", city: "" });
    setBranchModal({ mode: "new" });
  };

  const openEditBranch = (branch) => {
    setBranchForm({ name: branch.name || "", city: branch.city || "" });
    setBranchModal({ mode: "edit", branch });
  };

  const saveBranch = async () => {
    if (!branchForm.name.trim() || !branchForm.city.trim()) {
      setPageError("Şube ismi ve şehir zorunludur.");
      return;
    }
    setBranchSaving(true);
    setPageError("");
    try {
      const json = await apiPost("/savebranch", {
        subeId: branchModal?.branch?.subeId || branchModal?.branch?.branchNumber,
        name: branchForm.name,
        city: branchForm.city,
      });
      setBranches((current) => {
        const next = current.filter((item) => (item.subeId || item.branchNumber) !== (json.branch.subeId || json.branch.branchNumber));
        return [...next, json.branch].sort((a, b) => a.name.localeCompare(b.name, "tr"));
      });
      setBranchModal(null);
      setToast(branchModal?.mode === "edit" ? "Şube güncellendi." : "Şube kaydedildi ve 30 haneli numarası oluşturuldu.");
    } catch (error) {
      setPageError(error.message || "Şube kaydedilemedi");
    } finally {
      setBranchSaving(false);
    }
  };

  const deleteBranch = async (branch) => {
    const accepted = window.confirm(`${branch.name} şubesini ve bu şubeye bağlı tüm personelleri silmek istiyor musunuz?`);
    if (!accepted) return;
    try {
      await apiPost("/deletebranch", { subeId: branch.subeId || branch.branchNumber });
      setBranches((current) => current.filter((item) => (item.subeId || item.branchNumber) !== (branch.subeId || branch.branchNumber)));
      if (branchNumber === (branch.subeId || branch.branchNumber)) navigate("/branches");
      setToast("Şube ve bağlı personeller silindi.");
    } catch (error) {
      setPageError(error.message || "Şube silinemedi");
    }
  };

  const deletePersonnel = async (person) => {
    const content = person.localizedData?.find((item) => item.languageCode === "tr") || person.localizedData?.[0];
    const accepted = window.confirm(`${content?.firstName || "Bu"} personeli silmek istiyor musunuz?`);
    if (!accepted) return;
    try {
      await apiPost("/deletepersonnel", { subeId: branchNumber, personelId: person.personelId || person.id });
      setPersonnel((current) => current.filter((item) => item.id !== person.id));
      setToast("Personel silindi.");
    } catch (error) {
      setPageError(error.message || "Personel silinemedi");
    }
  };

  const handlePersonnelSaved = (saved) => {
    const mapped = mapPersonnelFromApi(saved);
    setPersonnel((current) => {
      const next = current.filter((item) => item.id !== mapped.id);
      return [...next, mapped].sort((a, b) => {
        const aName = a.localizedData?.find((item) => item.languageCode === "tr")?.firstName || "";
        const bName = b.localizedData?.find((item) => item.languageCode === "tr")?.firstName || "";
        return aName.localeCompare(bName, "tr");
      });
    });
    // Kaydetme sonrasında modal açık kalır. Yeni kayıtta oluşturulan personelId
    // forma geri yüklenir; sonraki Kaydet tıklaması yeni kayıt açmak yerine aynı
    // personeli günceller.
    setPersonnelModal({ mode: "edit", person: mapped });
    setToast("Personel kaydedildi.");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="font-black text-slate-900">Kiosk Merchant Panel</div>
          <nav className="flex flex-wrap gap-2">
            <button onClick={() => navigate("/")} className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700">Ürün Paneli</button>
            <button onClick={() => navigate("/designpanel")} className="rounded-xl bg-purple-600 px-4 py-2 text-sm font-bold text-white hover:bg-purple-700">Dizayn Paneli</button>
            <button onClick={() => navigate("/ReceiptInfoPage")} className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700">Fiş Bilgi</button>
            <button onClick={() => navigate("/preferance")} className="rounded-xl bg-teal-600 px-4 py-2 text-sm font-bold text-white hover:bg-teal-700">Tercihler</button>
            <button onClick={() => navigate("/excelfis")} className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-bold text-white hover:bg-amber-600">Excel Fiş</button>
            <button onClick={() => navigate("/appointments")} className="rounded-xl bg-fuchsia-600 px-4 py-2 text-sm font-bold text-white hover:bg-fuchsia-700">Randevular</button>
            <button className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white">Şubeler</button>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8">
        {pageError ? (
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 font-semibold text-rose-700">
            <span>{pageError}</span>
            <button onClick={() => setPageError("")} className="rounded-lg bg-white px-3 py-1.5 text-sm">Kapat</button>
          </div>
        ) : null}
        {toast ? <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 font-semibold text-emerald-800">{toast}</div> : null}

        {branchNumber && activeBranch ? (
          <PersonnelList
            branch={activeBranch}
            personnel={personnel}
            loading={loadingPersonnel}
            onBack={() => navigate("/branches")}
            onNew={() => setPersonnelModal({ mode: "new", person: null })}
            onEdit={(person) => setPersonnelModal({ mode: "edit", person })}
            onDelete={deletePersonnel}
          />
        ) : branchNumber && !loadingBranches ? (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-8 text-center">
            <h2 className="text-xl font-black text-amber-900">Şube bulunamadı</h2>
            <button onClick={() => navigate("/branches")} className="mt-4 rounded-xl bg-amber-600 px-5 py-3 font-bold text-white">Şubelere Dön</button>
          </div>
        ) : (
          <BranchList branches={branches} loading={loadingBranches} onOpen={(branch) => navigate(`/branches/${branch.subeId || branch.branchNumber}`)} onEdit={openEditBranch} onDelete={deleteBranch} onNew={openNewBranch} />
        )}
      </main>

      <Modal open={!!branchModal} title={branchModal?.mode === "edit" ? "Şubeyi Güncelle" : "Yeni Şube Kaydet"} subtitle="Yeni şubeye backend tarafından benzersiz 30 haneli numara atanır." onClose={() => setBranchModal(null)}>
        <div className="space-y-4">
          <label className="block space-y-2">
            <span className="text-sm font-black text-slate-700">Şube İsmi</span>
            <input value={branchForm.name} onChange={(event) => setBranchForm((current) => ({ ...current, name: event.target.value }))} className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500" placeholder="Örn. Kızılay Şubesi" />
          </label>
          <label className="block space-y-2">
            <span className="text-sm font-black text-slate-700">Şehir</span>
            <input value={branchForm.city} onChange={(event) => setBranchForm((current) => ({ ...current, city: event.target.value }))} className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500" placeholder="Örn. Ankara" />
          </label>
          {(branchModal?.branch?.subeId || branchModal?.branch?.branchNumber) ? (
            <div className="rounded-xl bg-slate-50 px-4 py-3">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500">30 Haneli Şube Numarası</div>
              <div className="mt-1 break-all font-mono text-sm text-slate-800">{branchModal.branch.subeId || branchModal.branch.branchNumber}</div>
            </div>
          ) : null}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setBranchModal(null)} className="rounded-xl border border-slate-300 px-5 py-3 font-bold text-slate-700">Vazgeç</button>
            <button onClick={saveBranch} disabled={branchSaving} className="rounded-xl bg-indigo-600 px-5 py-3 font-black text-white hover:bg-indigo-700 disabled:bg-slate-300">{branchSaving ? "Kaydediliyor..." : "Kaydet"}</button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!personnelModal && !!activeBranch}
        title={personnelModal?.mode === "edit" ? "Personel Bilgileri" : "Yeni Personel Ekle"}
        subtitle={activeBranch ? `${activeBranch.name} · ${activeBranch.city}` : ""}
        onClose={() => setPersonnelModal(null)}
        wide
      >
        {activeBranch && personnelModal ? (
          <PersonnelForm
            key={personnelModal.person?.personelId || personnelModal.person?.id || "new-personnel"}
            branch={activeBranch}
            initialValue={personnelModal.person}
            onClose={() => setPersonnelModal(null)}
            onSaved={handlePersonnelSaved}
          />
        ) : null}
      </Modal>
    </div>
  );
}
